import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { formatDateTime } from '../utils/format';
import './serviceRecordDetail.css';

const won = (n) => `${Number(n).toLocaleString()}원`;

const ServiceRecordDetailModal = ({ show, onHide, record }) => {
    if (!record) return null;

    // json 컬럼이라 보통 객체로 오지만, 문자열로 오는 경로가 있어 양쪽을 받는다.
    let breakdown = record.priceBreakdown;
    if (typeof breakdown === 'string') {
        try { breakdown = JSON.parse(breakdown); } catch { breakdown = null; }
    }

    const finalPrice = parseFloat(record.finalPrice) || 0;
    const paymentMethod = record.paymentMethod || breakdown?.paymentMethod || null;
    const comment = record.comment || record.memo || null;
    const adjustment = breakdown?.adjustment;
    const adjAmount = adjustment?.amount || 0;

    // 내역. items 는 스냅샷 시점의 가격표에서 떠온 줄 단위 금액이다(routes/jobs.js).
    // 옛 기록에는 없으므로 menuName·optionsText 로 이름만 복원해 같은 모양으로 맞춘다 —
    // 화면 구조가 데이터 유무에 따라 통째로 달라지면 읽는 사람이 헷갈린다.
    const rawItems = Array.isArray(breakdown?.items) ? breakdown.items : null;
    const menuName = record.menuName || record.serviceName || null;
    const optionNames = (record.optionsText || '').split(',').map(s => s.trim()).filter(Boolean);

    const menuLine = rawItems?.find(i => i.type === 'menu')
        || (menuName ? { name: menuName, price: null } : null);
    const optionLines = rawItems?.filter(i => i.type === 'option')
        || optionNames.map(name => ({ name, price: null }));

    // 줄마다 금액을 보여줄 조건: 모든 줄에 금액이 있고, 합 + 조정이 최종가와 맞을 것.
    // finalPrice = 메뉴 + 옵션들 + 조정 이므로(utils/priceCalculator.js) 정상이면 맞는다.
    // 안 맞는데도 그리면 "합이 틀린 영수증" 이 되고, 그건 아무것도 안 보여주는 것보다 나쁘다.
    const lines = [menuLine, ...optionLines].filter(Boolean);
    const allPriced = lines.length > 0 && lines.every(l => l.price != null);
    const itemsSum = allPriced ? lines.reduce((sum, l) => sum + Number(l.price), 0) : null;
    const showAmounts = allPriced && itemsSum + adjAmount === finalPrice;

    const customerName = record.linkedCustomerName || record.customerName || null;
    const plateNumber = record.linkedPlateNumber || record.plateNumber || null;
    const carModel = [
        record.manufacturer || record.temp_manufacturer,
        record.modelName || record.temp_model,
    ].filter(Boolean).join(' ');
    const workedAt = record.startTime || record.created_at || null;

    const who = [
        ['고객', customerName],
        ['차량', plateNumber],
        ['연락처', record.phoneNumber],
        ['차종', [carModel, record.sizeName].filter(Boolean).join(' · ')],
        ['담당', record.staffName],
        ['일시', workedAt ? formatDateTime(workedAt) : null],
    ].filter(([, v]) => v);

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    서비스 상세 내역
                    {/* 고객센터 문의 폼이 "손님 이름·연락처 대신 차량번호나 작업 번호로
                        알려주세요" 라고 안내하는데, 정작 그 번호를 띄우는 화면이 없었다.
                        순번이 아니라 조회용 고유번호다 — TiDB 의 AUTO_INCREMENT 가 노드별
                        캐시로 할당돼 3만 단위로 뛰므로 '몇 번째 작업' 으로 읽히지 않는다.
                        전역 id 를 그대로 노출해도 되는 건 Jobs 접근이 전부
                        `WHERE id = ? AND shop_id = ?` 로 묶여 있어서다(routes/jobs.js). */}
                    {record.id != null && (
                        <span className="srd-job-no">작업번호 #{record.id}</span>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* 상세인데 "누구 차인가" 가 없었다. 그게 맨 위로 온다. */}
                {who.length > 0 && (
                    <div className="srd-who">
                        {who.map(([label, value]) => (
                            <div className="srd-who-row" key={label}>
                                <span className="srd-who-label">{label}</span>
                                <span className="srd-who-value">{value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {menuLine && (
                    <div className="srd-group">
                        <div className="srd-group-label">메뉴</div>
                        <div className="srd-line">
                            <span className="srd-line-name">{menuLine.name}</span>
                            {showAmounts && <span className="srd-line-amount">{won(menuLine.price)}</span>}
                        </div>
                    </div>
                )}

                {optionLines.length > 0 && (
                    <div className="srd-group">
                        <div className="srd-group-label">옵션</div>
                        {optionLines.map((opt, idx) => (
                            <div className="srd-line" key={opt.id ?? idx}>
                                <span className="srd-line-name">{opt.name}</span>
                                {showAmounts && <span className="srd-line-amount">+{won(opt.price)}</span>}
                            </div>
                        ))}
                    </div>
                )}

                {adjAmount !== 0 && (
                    <div className="srd-group">
                        <div className="srd-group-label">조정</div>
                        <div className="srd-line">
                            <span className="srd-line-name">
                                {adjAmount > 0 ? '추가' : '할인'}
                                {adjustment?.reason && (
                                    <span className="srd-line-reason">{adjustment.reason}</span>
                                )}
                            </span>
                            <span className={`srd-line-amount ${adjAmount > 0 ? 'is-plus' : 'is-minus'}`}>
                                {adjAmount > 0 ? '+' : '−'}{won(Math.abs(adjAmount))}
                            </span>
                        </div>
                    </div>
                )}

                {/* 줄별 금액을 못 보여줄 때는 이유를 쓴다. 옛 기록은 스냅샷에 금액이 없고,
                    차량 사이즈를 모르면 가격표를 끌 수 없다. */}
                {lines.length > 0 && !showAmounts && (
                    <div className="srd-note">
                        이 기록에는 항목별 금액이 남아 있지 않습니다. 최종 합계만 표시합니다.
                    </div>
                )}

                <div className="srd-rule" />

                <div className="srd-total">
                    <span>{paymentMethod && <span className="srd-pay">{paymentMethod}</span>}</span>
                    <span className="text-end">
                        <div className="srd-total-label">최종 합계</div>
                        <div className="srd-total-amount">{won(finalPrice)}</div>
                    </span>
                </div>

                {comment && (
                    <div className="srd-memo">
                        <div className="srd-memo-label">메모</div>
                        <div className="srd-memo-body">{comment}</div>
                    </div>
                )}

                {breakdown?.confirmedBy?.username && (
                    <div className="srd-foot">확정: {breakdown.confirmedBy.username}</div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>닫기</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ServiceRecordDetailModal;
